import * as Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import * as React from 'react'
import { useSearchParams } from 'react-router-dom'
import * as Api from 'src/api'
import { createBarChartOptions, useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useTranslatable } from 'src/hooks/locale/utils'
import { useActiveSemester } from 'src/hooks/semesters'
import { useTheme } from 'src/state/providers/Theme'
import Select from 'src/views/components/Select'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'

export default function AdministratorStatisticChoicesPage(): JSX.Element | null {
  const t = useTranslatable()

  return (
    <SuspenseWrapper title={t('statistics:statistics_choices')}>
      <PageContent />
    </SuspenseWrapper>
  )
}

function PageContent(): JSX.Element | null {
  const t = useTranslatable()
  const activeSemester = useActiveSemester()
  const [searchParams, setSearchParams] = useSearchParams({
    semId: activeSemester?.id as string,
  })
  const semId = searchParams.get('semId')
  const headers = useAuthenticatedHeaders()
  const isDarkMode = useTheme() === 'dark'

  const { data: semesters, isValidating: isValidatingSemesters } = useApi({
    endpoint: Api.getSemesters,
    params: React.useMemo(() => ({ headers }), [headers]),
  })

  const selectedSemester = React.useMemo(
    () => semesters?.filter((semester) => semester.id === semId)[0],
    [semesters, semId]
  )

  return (
    <div className="flex w-full flex-col items-center">
      <Select
        className="my-2 mr-2 w-full sm:w-[70%] xxs:max-w-full xs:max-w-full"
        value={selectedSemester}
        options={semesters ?? []}
        getOptionValue={(option) => option.id}
        getOptionLabel={(option) => option.name + (option.isActive ? t('common:current') : '')}
        onChange={(option) => setSearchParams({ semId: option!.id })}
        isDisabled={isValidatingSemesters}
      />
      <SuspenseWrapper>
        <ChoiceChart semId={semId} isDarkMode={isDarkMode} />
      </SuspenseWrapper>
    </div>
  )
}

interface ChartProps {
  semId: string | null
  isDarkMode: boolean
}

function ChoiceChart({ semId, isDarkMode }: ChartProps): JSX.Element | null {
  const headers = useAuthenticatedHeaders()
  const t = useTranslatable()
  const { data } = useApi({
    endpoint: Api.getAdministratorStatisticsChoices,
    params: React.useMemo(() => ({ headers, query: { semId: semId! } }), [headers, semId]),
    shouldFetch: semId != null,
  })

  const chartTitle = `${t('common:choices')} (${t('common:total')} ${data?.total ?? 0})`

  const options = React.useMemo(
    () => createBarChartOptions(data, chartTitle, isDarkMode),
    [data, chartTitle, isDarkMode]
  )

  if (!data) return null

  return (
    <div className="chart size-full pt-5">
      <HighchartsReact highcharts={Highcharts} options={options} />
    </div>
  )
}
